import React, { useEffect } from 'react'
import { Layout } from 'app/theme/layout'
import { Helmet } from 'react-helmet-async'
import posthog from 'posthog-js'
import { FoodTruckCatering, SaveTime, SitBackRelax } from 'app/theme/sections'
import { RepeatRepeat, MakeEveryoneHappy, WhyWorkWithUs } from 'app/theme/sections'
import { CateringForAnyEvent, HowItWorks, HowItWorksLarge, FeaturedClients, CuisineTypes } from 'app/theme/sections'
import Calculator from 'app/theme/calculator'
import LeadModal from 'app/leads/LeadModal'
import { useModalState } from 'lib/modal'
import styles from './FoodTruckCateringPage.module.css'
import foodTruckServingCustomer from 'assets/img/roaming_hunger_la-37_fixed_v2.jpg'

export default function FoodTruckCateringPage(props) {
  const { openModal, ...modalState } = useModalState()

  useEffect(() => {
    posthog.startSessionRecording()
    return () => {
      posthog.stopSessionRecording()
    }
  }, [])

  const googleMetaTags = JSON.stringify({
    '@context': 'http://schema.org',
    '@type': 'FoodService',
    'subjectOf': {
      '@type': 'FAQPage',
      url: `https://roaminghunger.com/food-truck-catering`,
      'mainEntity': [
        {
          '@type': 'Question',
          'name': 'How much does food truck catering cost?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': '<p>On average food truck catering costs $1,541, and pricing for a catering package can typically range $800-$2,500.&nbsp;</p> <p>Cuisine affects the costs of your food truck catering more than any other factor. Here are typical per person costs along with average minimums.</p> <div align=\'left\'> <table> <tbody> <tr> <td> <strong>Cuisine</strong> </td> <td> <strong>Average Price per Person</strong> </td> <td> <strong>1-100 Eaters (Minimum)</strong> </td> </tr> <tr> <td> <a href=\'https://roaminghunger.com/food-truck-catering/taco-truck-catering/\'>Taco Truck Catering</a> </td> <td> $10-15 </td> <td> $1,000 </td> </tr> <tr> <td> <a href=\'https://roaminghunger.com/food-truck-catering/burger-catering-truck/\'>Burger</a>,&nbsp;<a href=\'https://roaminghunger.com/food-truck-catering/bbq-food-truck-catering/\'>BBQ</a>, &amp;&nbsp;<a href=\'https://roaminghunger.com/food-truck-catering/pizza-truck-catering/\'>Pizza</a> Truck Catering </td> <td> $15-25 </td> <td> $1,500 </td> </tr> <tr> <td> Dessert Truck Catering </td> <td> $8-20 </td> <td> $800 </td> </tr> <tr> <td> Beverage Truck Catering </td> <td> $8-20 </td> <td> $800 </td> </tr> </tbody> </table> </div> <p>Use our&nbsp;<a href=\'https://roaminghunger.com/food-truck-catering/#calculator\'>Food Truck Catering Cost Calculator</a></p> <p>Adding drinks or dessert options (with a savory food truck) will increase the price per person by $4-$5.</p> <p>If you&rsquo;re having a small party of 50 guests or less, you&rsquo;ll probably be affected by the food truck catering minimums, and the price per person will increase.</p> <p>Typical service times are 2-3 hours. Additional hours will increase total costs. Travel fees can also affect pricing. Food trucks may charge a per mile fee if your event is outside their normal travel radius (20-30 miles).&nbsp;</p> <p>This data is based on actual Roaming Hunger data, and does not include any taxes or fees.</p>'
          }
        },
        {
          '@type': 'Question',
          'name': 'What are some popular cuisines for food truck catering?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': '<p>Here are our top 10 most requested food truck cuisines:</p> <ul> <li> <a href=\'https://roaminghunger.com/food-truck-catering/taco-truck-catering/\'>Tacos</a> (Mexican and Fusion) </li> <li> <a href=\'https://roaminghunger.com/food-truck-catering/burger-catering-truck/\'>Burgers</a> <li> <a href=\'https://roaminghunger.com/food-truck-catering/bbq-food-truck-catering/\'>BBQ</a> </li> <li> <a href=\'https://roaminghunger.com/food-truck-catering/pizza-truck-catering/\'>Pizza</a> </li> <li>Sandwiches</li> <li>Asian</li> <li>Dessert&nbsp;</li> <li>Healthy Food</li> <li>Mediterranean</li> <li>Breakfast</li> </ul> <p>We also get many requests for Vegetarian and Vegan options, which many food trucks can accommodate.</p>'
          }
        },
        {
          '@type': 'Question',
          'name': 'How do I get catering packages for food trucks near me?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': '<p>If your event details are confirmed, and your budget meets the minimum (see above), you can get catering packages and quotes by submitting a catering request.</p> <p>Your request will be sent to food trucks in your area, and available trucks will submit a catering package with pricing and menu. From there you can choose the best catering package for you.</p>'
          }
        },
        {
          '@type': 'Question',
          'name': 'Why should I use Roaming Hunger?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': '<p>Roaming Hunger is the largest website for food trucks and mobile food businesses. Since 2009, we&rsquo;ve helped thousands of companies and individuals book food trucks for catering and events. Today, we have over 23,000 independent food trucks, trailers, carts, and pop-ups on our site.</p> <ul> <li> <p><strong>Variety</strong>&nbsp;- Our network consists of hundreds of local vendors in every major city, totaling 23,000+ nationally.</p> </li> <li> <p><strong>Save Time</strong>&nbsp;- Why have 100 conversations to find one food truck when you can have one conversation to find 100 food trucks?!</p> </li> <li> <p><strong>Speed</strong>&nbsp;- Whether you are ready to book one day or one month out from your event, we will provide every available food truck option.</p> </li> <li> <p><strong>Quality Options </strong>- All the food trucks we work with are gourmet, vetted, licensed, proven and reliable.</p> </li> <li> <p><strong>Service</strong> - Every catering is protected by our 100% Roaming Hunger Service Guarantee. If the caterer does not show, we&rsquo;ll get you a replacement caterer or refund 100% of your money.</p> </li> </ul>'
            }
        }
      ],
      'name': 'Food Truck Catering FAQ',
      '@id': 'https://roaminghunger.com/food-truck-catering/#FAQPage'
    },
    'additionalType': [
      'https://www.wikidata.org/wiki/Q3303463',
      'https://www.wikidata.org/wiki/Q6887072'
    ],
    'image': [
      'https://d3hbe0kmbam4a5.cloudfront.net/assets/static/media/nachos.bca57378.jpg',
      'https://d3hbe0kmbam4a5.cloudfront.net/assets/static/media/wraps.85b3c0eb.jpg',
      'https://d3hbe0kmbam4a5.cloudfront.net/assets/static/media/how-it-works-large.b331beb9.jpg',
      'https://d3hbe0kmbam4a5.cloudfront.net/assets/static/media/tacos.64c4e3ca.jpg',
      'https://d3hbe0kmbam4a5.cloudfront.net/assets/static/media/fries.349dba3f.jpg',
      'https://d3hbe0kmbam4a5.cloudfront.net/assets/static/media/ice-cream.5af9f7b7.jpg'
    ],
    'name': 'Food Truck Catering',
    'description': 'Hire the right gourmet food truck for your next catered event',
    'url': 'https://roaminghunger.com/food-truck-catering/',
    'isSimilarTo': [
      {
        '@type': 'FoodService',
        'url': 'https://roaminghunger.com/food-truck-catering/bbq-food-truck-catering/',
        'name': 'BBQ Truck Catering',
        '@id': 'https://roaminghunger.com/food-truck-catering/bbq-food-truck-catering/#FoodService'
      },
      {
        '@type': 'FoodService',
        'url': 'https://roaminghunger.com/food-truck-catering/pizza-truck-catering/',
        'name': 'Pizza Truck Catering',
        '@id': 'https://roaminghunger.com/food-truck-catering/pizza-truck-catering/#FoodService'
      }
    ],
    '@id': 'https://roaminghunger.com/food-truck-catering/#FoodService',
  })

  return (
    <Layout>
      <Helmet>
        <title>Food Truck Catering - Corporate & Event Catering - Roaming Hunger</title>
        <meta name="description" content="Book a food truck for your next event with Roaming Hunger. You give us the details and we find the best food trucks for you." />
        <meta name="og:title" content="Food Truck Catering - Corporate & Event Catering"></meta>
        <script type="application/ld+json">
          {googleMetaTags}
        </script>
      </Helmet>
      <LeadModal
        {...modalState}
      />
      <FoodTruckCatering
        jumpTarget="#save-time"
        className={styles.hero_background_image}
        imageUrl={foodTruckServingCustomer}
        onButtonClick={openModal}
        role="img"
        aria-label="A cheerful crowd stands in front of a food truck, with one customer being served and others excitedly chatting"
      />
      <SaveTime
        id="save-time"
        jumpTarget="#make-everyone-happy"
        onButtonClick={openModal}
      />
      <MakeEveryoneHappy
        id="make-everyone-happy"
        jumpTarget="#sit-back-relax"
        onButtonClick={openModal}
      />
      <SitBackRelax
        id="sit-back-relax"
        jumpTarget="#why-work-with-us"
        onButtonClick={openModal}
      />
      <WhyWorkWithUs
        id="why-work-with-us"
        onButtonClick={openModal}
      />
      <RepeatRepeat
        id="repeat-repeat"
        jumpTarget="#catering-any-event"
        onButtonClick={openModal}
      />
      <CateringForAnyEvent
        id="catering-any-event"
      />
      <CuisineTypes
        baseUrl='/food-truck-catering'
      />
      <HowItWorks
        onButtonClick={openModal}
      />
      <HowItWorksLarge
        onButtonClick={openModal}
      />
      <Calculator
        id="calculator"
        onButtonClick={openModal}
      />
      <FeaturedClients
        onButtonClick={openModal}
      />
      <div className="bg-navy">
        <div className="container p-6">
          <div className="flex w-full">
            <div className="w-full py-16">
              <h2 className="text-white text-4xl leading-none font-platform text-center">
                Frequently Asked Questions
              </h2>
              <div className="w-full md:w-4/5 mb-2 mt-6 m-auto">
                <h3 className="text-white text-3xl">How much does food truck catering cost?</h3>
                <div className={[styles.faq, 'text-white text-lg']}>
                  <p>
                    Food truck catering packages typically range from $1,000-$3,000, depending on guest count, cuisine, and
                    length of service.
                  </p>
                  <p>
                    Cuisine affects the costs of your food truck catering more than any other factor. Here
                    are typical per-person prices along with average minimums.
                  </p>
                  <table className="w-full table-auto border-spacing-px border-collapse border border-navy-300 my-4">
                    <thead>
                      <tr>
                        <th className="text-left p-2 border border-navy-300">Cuisine</th>
                        <th className="text-left p-2 border border-navy-300">Average Price per Person</th>
                        <th className="text-left p-2 border border-navy-300">1-100 Eaters (Minimum)</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="p-2 border border-navy-300 font-light">
                          <a href="/food-truck-catering/taco-truck-catering/" target="blank" className="text-blue-400">Taco Truck Catering</a>
                        </td>
                        <td className="p-2 border border-navy-300 font-light">$15-25</td>
                        <td className="p-2 border border-navy-300 font-light">$1,200</td>
                      </tr>
                      <tr>
                        <td className="p-2 border border-navy-300 font-light">
                          <a href="/food-truck-catering/bbq-food-truck-catering/" target="blank" className="text-blue-400">BBQ Truck Catering</a>
                        </td>
                        <td className="p-2 border border-navy-300 font-light">$21-34</td>
                        <td className="p-2 border border-navy-300 font-light">$1,500</td>
                      </tr>
                      <tr>
                        <td className="p-2 border border-navy-300 font-light">
                          <a href="/food-truck-catering/burger-catering-truck/" target="blank" className="text-blue-400">Burger Truck Catering</a>
                        </td>
                        <td className="p-2 border border-navy-300 font-light">$16-27</td>
                        <td className="p-2 border border-navy-300 font-light">$1,500</td>
                      </tr>
                      <tr>
                        <td className="p-2 border border-navy-300 font-light">
                          <a href="/food-truck-catering/pizza-truck-catering/" target="blank" className="text-blue-400">Pizza Truck Catering</a>
                        </td>
                        <td className="p-2 border border-navy-300 font-light">$20-28</td>
                        <td className="p-2 border border-navy-300 font-light">$1,500</td>
                      </tr>
                      <tr>
                        <td className="p-2 border border-navy-300 font-light">Mediterranean Truck Catering</td>
                        <td className="p-2 border border-navy-300 font-light">$18-28</td>
                        <td className="p-2 border border-navy-300 font-light">$1,500</td>
                      </tr>
                      <tr>
                        <td className="p-2 border border-navy-300 font-light">Asian Truck Catering</td>
                        <td className="p-2 border border-navy-300 font-light">$18-28</td>
                        <td className="p-2 border border-navy-300 font-light">$1,500</td>
                      </tr>
                      <tr>
                        <td className="p-2 border border-navy-300 font-light">Indian Truck Catering</td>
                        <td className="p-2 border border-navy-300 font-light">$18-28</td>
                        <td className="p-2 border border-navy-300 font-light">$1,500</td>
                      </tr>
                      <tr>
                        <td className="p-2 border border-navy-300 font-light">Dessert Truck Catering</td>
                        <td className="p-2 border border-navy-300 font-light">$10-20</td>
                        <td className="p-2 border border-navy-300 font-light">$800</td>
                      </tr>
                      <tr>
                        <td className="p-2 border border-navy-300 font-light">Beverage Truck Catering</td>
                        <td className="p-2 border border-navy-300 font-light">$8-18</td>
                        <td className="p-2 border border-navy-300 font-light">$800</td>
                      </tr>
                    </tbody>
                  </table>
                  <p>
                    Use our <a href="#calculator" className="text-blue-400 underline">Food Truck Catering Cost Calculator</a>
                  </p>
                  <p>
                    Adding drinks or dessert options (with a savory food truck) will increase the price per
                    person by $4-$5.
                  </p>
                  <p>
                    If you're having a small party of 50 guests or less, you'll probably be affected by the
                    food truck catering minimums, and the price per person will increase.
                  </p>
                  <p>
                    Typical service times are 2-3 hours. Additional hours will increase total costs. Travel
                    fees can also affect pricing. Food trucks may charge a per-mile fee if your event is
                    outside their normal travel radius (20-30 miles).
                  </p>
                  <p>
                    This data is based on historical Roaming Hunger catering bookings and does not include
                    any taxes or fees.
                  </p>
                </div>

                <h3 className="text-white text-3xl mt-4">What are some popular cuisines for food truck catering?</h3>
                <div className={[styles.faq, 'text-white text-lg']}>
                  <p>
                    Here are our top 10 most requested food truck cuisines:
                  </p>
                  <ul className="pl-2 list-disc list-inside">
                    <li className="font-light">
                      <a href="/food-truck-catering/taco-truck-catering/" target="blank" className="text-blue-400">Tacos</a> (Mexican and Fusion)
                    </li>
                    <li className="font-light">
                      <a href="/food-truck-catering/burger-catering-truck/" target="blank" className="text-blue-400">Burgers</a>
                    </li>
                    <li className="font-light">
                      <a href="/food-truck-catering/bbq-food-truck-catering/" target="blank" className="text-blue-400">BBQ</a>
                    </li>
                    <li className="font-light">
                      <a href="/food-truck-catering/pizza-truck-catering/" target="blank" className="text-blue-400">Pizza</a>
                    </li>
                    <li>Sandwiches</li>
                    <li>Asian</li>
                    <li>Dessert&nbsp;</li>
                    <li>Healthy Food</li>
                    <li>Mediterranean</li>
                    <li>Breakfast</li>
                  </ul>
                  <p>
                    We also get many requests for Vegetarian and Vegan options, which many food trucks
                    can accommodate.
                  </p>
                </div>
                <h3 className="text-white text-3xl mt-4">How do I get catering packages for food trucks near me?</h3>
                <div className={[styles.faq, 'text-white text-lg']}>
                  <p>
                    If your event details are confirmed, and your budget meets the minimum (see above), you can
                    get catering packages and quotes by submitting a catering request.
                  </p>
                  <p>
                    Roaming Hunger will send your request to food trucks in your area, and available trucks will
                    submit a catering package with pricing and menu. From there you can choose the best catering
                    package for you.
                  </p>
                </div>
                <h3 className="text-white text-3xl mt-4">Why should I use Roaming Hunger?</h3>
                <div className={[styles.faq, 'text-white text-lg']}>
                  <p>
                    Roaming Hunger is the largest website for food trucks and mobile food businesses. Since 2009,
                    we’ve helped thousands of companies and individuals book food trucks for catering and events.
                    Today, we have over 23,000 independent food trucks, trailers, carts, and pop-ups on our site.
                  </p>
                  <ol className="pl-6 list-disc list-outside">
                    <li><strong>Variety</strong> - Our network consists of hundreds of local vendors in every major city, totaling 23,000+ nationally.</li>
                    <li><strong>Save Time</strong> - Why have 100 conversations to find one food truck when you can have one conversation to find 100 food trucks?!</li>
                    <li><strong>Speed</strong> - Whether you are ready to book one day or one month out from your event, we will provide every available food truck option.</li>
                    <li><strong>Quality Options</strong> - All the food trucks we work with are gourmet, vetted, licensed, proven and reliable.</li>
                    <li><strong>Service</strong> - Every catering is protected by our 100% Roaming Hunger Service Guarantee. If the caterer does not show, we’ll get you a replacement caterer or refund 100% of your money.</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  )
}
